import type React from "react"
import { useState } from "react"
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Stack,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material"
import {
  Save as SaveIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
} from "@mui/icons-material"
import { updateUser } from "../../apis/userApi"
import { toast } from "react-toastify"
import { backendURL } from "../../url"
import axios from "axios"

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

const SettingsSection: React.FC<{ user?: any }> = ({ user }) => {
  const [tabValue, setTabValue] = useState(0)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)
  const [firstName, setFirstName] = useState(user?.firstName)
  const [lastName, setLastName] = useState(user?.lastName)
  const [email, setEmail] = useState(user?.email)
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber)
  const [address, setAddress] = useState(user?.address)
  const [newPassword, setNewPassword] = useState("")
  const [confirmNewPassword, setConfirmNewPassword] = useState("")
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false)

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    
    // Check if any data has actually changed
    const hasDataChanged = 
      firstName !== user?.firstName ||
      lastName !== user?.lastName ||
      email !== user?.email ||
      phoneNumber !== user?.phoneNumber ||
      address !== user?.address

    if (!hasDataChanged) {
      toast.info("No changes detected. Nothing to update.")
      return
    }

    // Validate required fields
    if (!firstName?.trim() || !lastName?.trim() || !email?.trim()) {
      toast.error("First name, last name, and email are required fields")
      return
    }

    setIsUpdatingProfile(true)
    const userData = {
      firstName,
      lastName,
      email,
      phoneNumber,
      address
    }
    const response = await updateUser(user?.id, userData)
    if (response.message === "User updated successfully") {
      setFormSubmitted(true)
      toast.success("Settings updated successfully")
    } else {
      toast.error("Failed to update settings")
    }
    setFormSubmitted(true)

    // Reset form submitted state after 3 seconds
    setTimeout(() => {
      setFormSubmitted(false)
    }, 3000)
    setIsUpdatingProfile(false)
  }

  const handleUpdatePassword = async (event: React.FormEvent) => {
    event.preventDefault()
    
    // Check if password fields are empty
    if (!newPassword?.trim() || !confirmNewPassword?.trim()) {
      toast.error("Please enter both new password and confirm password")
      return
    }

    if(newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match")
      return
    }

    // Check if password meets minimum requirements (optional but recommended)
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long")
      return
    }
    
    setIsUpdatingPassword(true)
    const passwordData = {
      id: user?.id,
      newPassword: confirmNewPassword,
    }
    try {
      const response = await axios.post(`${backendURL}/api/auth/reset-password`, passwordData)
      if (response.status === 200) {
        toast.success("Password updated successfully")
        setNewPassword("")
        setConfirmNewPassword("")
      }
    } catch (error: any) { 
      if(error.response.status === 400) {
        toast.error("New password cannot be same as old password")
      } else {
        toast.error("Failed to update password")
      }
    } finally {
      setIsUpdatingPassword(false)
    }
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Settings
      </Typography>

      <Paper sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="settings tabs" sx={{ px: 2 }}>
            <Tab icon={<PersonIcon />} label="Profile" iconPosition="start" />
            <Tab icon={<SecurityIcon />} label="Security" iconPosition="start" />
            {/* <Tab icon={<NotificationsIcon />} label="Notifications" iconPosition="start" /> */}
          </Tabs>
        </Box>

        {formSubmitted && (
          <Alert severity="success" sx={{ mx: 3, mt: 3 }}>
            Settings updated successfully!
          </Alert>
        )}

        <Box sx={{ px: 3 }}>
          <TabPanel value={tabValue} index={0}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} variant="outlined" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} variant="outlined" />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="outlined"
                    type="email"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Phone Number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} variant="outlined" />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" startIcon={isUpdatingProfile ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} disabled={isUpdatingProfile}>
                    {isUpdatingProfile ? "Updating Profile..." : "Save Changes"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Stack spacing={3}>
              <Typography variant="h6">Change Password</Typography>

              <Grid container spacing={3}>
                {/* <Grid item xs={12}>
                  <TextField fullWidth label="Current Password" type="password" variant="outlined" sx={{ width: "fit-content" }}/>
                </Grid> */}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="New Password" type="password" variant="outlined" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Confirm New Password" type="password" variant="outlined" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} />
                </Grid>
              </Grid>

              <Box>
                <Button 
                  variant="contained" 
                  onClick={handleUpdatePassword} 
                  startIcon={isUpdatingPassword ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={isUpdatingPassword}
                >
                  {isUpdatingPassword ? "Updating Password..." : "Update Password"}
                </Button>
              </Box>
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              Email Notifications
            </Typography>

            <Stack spacing={2}>
              <FormControlLabel control={<Switch defaultChecked />} label="Booking confirmations" />
              <FormControlLabel control={<Switch defaultChecked />} label="Booking reminders" />
              <FormControlLabel control={<Switch defaultChecked />} label="Membership renewals" />
              <FormControlLabel control={<Switch />} label="Promotional emails" />
              <FormControlLabel control={<Switch defaultChecked />} label="Account updates" />
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Push Notifications
            </Typography>

            <Stack spacing={2}>
              <FormControlLabel control={<Switch defaultChecked />} label="Booking reminders" />
              <FormControlLabel control={<Switch />} label="New features" />
              <FormControlLabel control={<Switch defaultChecked />} label="Special offers" />
            </Stack>

            <Box sx={{ mt: 3 }}>
              <Button variant="contained" startIcon={<SaveIcon />} >
                Save Preferences
              </Button>
            </Box>
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  )
}

export default SettingsSection
